import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { themes, applyTheme, initializeTheme } from '../utils/themeManager';
import '../styles/components/Header.css';

const Header: React.FC = () => {
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [currentTheme, setCurrentTheme] = useState<string>('cyan-pink');
  const [showThemePicker, setShowThemePicker] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    // Initialize theme on component mount
    const savedTheme = initializeTheme();
    setCurrentTheme(savedTheme);
  }, []);

  useEffect(() => {
    // Check for theme query parameter
    const urlParams = new URLSearchParams(location.search);
    const hasThemeParam = urlParams.has('theme');
    setShowThemePicker(hasThemeParam);
  }, [location.search]);


  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isThemeMenuOpen && !target.closest('.theme-dropdown')) {
        setIsThemeMenuOpen(false);
      }
      if (isMobileMenuOpen && !target.closest('.mobile-menu-container') && !target.closest('.hamburger-menu')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isThemeMenuOpen, isMobileMenuOpen]);

  const handleThemeChange = (themeId: string): void => {
    setCurrentTheme(themeId);
    applyTheme(themeId);
    setIsThemeMenuOpen(false);
  };

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="nav-container">
      <div className="band-logo">
        <Link to="/" onClick={closeMobileMenu}>
          <img 
            src="/LS_Logo_Text_Only_wht.png" 
            alt="Lost Signal" 
            className="logo-image"
          />
        </Link>
      </div>
      
      {/* Hamburger Menu Button */}
      <button 
        className={`hamburger-menu ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={isMobileMenuOpen}
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Desktop Navigation */}
      <nav className="desktop-nav">
        <ul className="nav-menu">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/songlist">Songlist</Link></li>
          <li><Link to="/videos">Videos</Link></li>
          <li><Link to="/images">Images</Link></li>
          <li><Link to="/contact">Contact</Link></li>
          {showThemePicker && (
            <li className="theme-dropdown-container">
              <div className="theme-dropdown">
                <button 
                  id="theme-dropdown-toggle" 
                  className={`theme-dropdown-toggle ${isThemeMenuOpen ? 'active' : ''}`}
                  aria-label="Theme selector"
                  onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                >
                  <i className="fas fa-palette theme-icon"></i>
                  <span className="dropdown-arrow">▼</span>
                </button>
                <div className={`theme-dropdown-menu ${isThemeMenuOpen ? 'show' : ''}`} id="theme-dropdown-menu">
                  {themes.map(theme => (
                    <div 
                      key={theme.id}
                      className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`} 
                      data-theme={theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                    >
                      <div className={`theme-preview theme-preview-${theme.id}`}></div>
                      <span>{theme.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </li>
          )}
        </ul>
      </nav>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-menu-container ${isMobileMenuOpen ? 'active' : ''}`}>
        <nav className="mobile-nav">
          <ul className="mobile-nav-menu">
            <li><Link to="/" onClick={closeMobileMenu}>Home</Link></li>
            <li><Link to="/songlist" onClick={closeMobileMenu}>Songlist</Link></li>
            <li><Link to="/videos" onClick={closeMobileMenu}>Videos</Link></li>
            <li><Link to="/images" onClick={closeMobileMenu}>Images</Link></li>
            <li><Link to="/contact" onClick={closeMobileMenu}>Contact</Link></li>
            <li className="mobile-theme-section">
              <div className="mobile-theme-label">Theme</div>
              <div className="mobile-theme-options">
                {themes.map(theme => (
                  <button
                    key={theme.id}
                    className={`mobile-theme-option ${currentTheme === theme.id ? 'active' : ''}`}
                    onClick={() => {
                      handleThemeChange(theme.id);
                      closeMobileMenu();
                    }}
                  >
                    <div className={`theme-preview theme-preview-${theme.id}`}></div>
                    <span>{theme.name}</span>
                  </button>
                ))}
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Header;
